import { useTranslation } from "react-i18next";
import styles from "./ActivityStatusTab.module.css";
import LoadingSpinner from "../../UI/LoadingSpinner/LoadingSpinner";
import DefaultUserImage from "../../../assets/images/default-user.webp";

export default function ActivityStatusTab({
  allUsers,
  loadingUsers,
  usersError,
  fetchUsers,
  hasFetchedRef,
  invitations,
  handleCancelInvitation,
}) {
  const { t } = useTranslation("common");

  const roleBadgeClass = (role) => {
    const r = String(role || "").toLowerCase();
    if (r === "owner") return styles.roleOwner;
    if (r === "admin") return styles.roleAdmin;
    return styles.roleUser;
  };

  return (
    <div className={styles.tabContent}>
      <div className={styles.sectionHeader}>
        <div>
          <h2 className={styles.sectionTitle}>
            {t("userInfoPage.activityStatus.title")}
          </h2>
          <p className={styles.sectionDescription}>
            {t("userInfoPage.activityStatus.description")}
          </p>
        </div>
        <button
          type="button"
          className={styles.headerActionButton}
          onClick={() => {
            hasFetchedRef.current = false;
            fetchUsers();
          }}
        >
          {t("refresh")}
        </button>
      </div>

      {loadingUsers && <LoadingSpinner />}

      {usersError && (
        <div className={styles.errorContainer}>
          <p className={styles.errorMessage}>
            {t("error")}: {usersError}
          </p>
          <button
            type="button"
            className={styles.retryButton}
            onClick={() => {
              hasFetchedRef.current = false;
              fetchUsers();
            }}
          >
            {t("retry")}
          </button>
        </div>
      )}

      {!loadingUsers && !usersError && allUsers && allUsers.length > 0 && (
        <>
          <div
            className={`${styles.tableContainer} ${styles.tableContainerDesktopOnly}`}
          >
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>{t("userInfoPage.activityStatus.tableHeaders.user")}</th>
                  <th>{t("userInfoPage.activityStatus.tableHeaders.email")}</th>
                  <th>{t("userInfoPage.activityStatus.tableHeaders.role")}</th>
                  <th>
                    {t("userInfoPage.activityStatus.tableHeaders.account")}
                  </th>
                  <th>
                    {t("userInfoPage.activityStatus.tableHeaders.lastLogin")}
                  </th>
                  <th>
                    {t("userInfoPage.activityStatus.tableHeaders.joined")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {allUsers.map((user) => (
                  <tr key={user._id}>
                    <td className={styles.userCell}>
                      <div className={styles.userInfo}>
                        <div className={styles.userAvatar}>
                          {user.profileImage ? (
                            <img
                              src={user.profileImage}
                              alt={user.name || "User"}
                              className={styles.profileImage}
                              onError={(e) => {
                                e.currentTarget.onerror = null;
                                e.currentTarget.src = DefaultUserImage;
                              }}
                            />
                          ) : (
                            (user.name || "U").charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className={styles.userName}>
                          {user.name || "No Name"}
                        </div>
                      </div>
                    </td>
                    <td className={styles.emailCell}>{user.email}</td>
                    <td className={styles.roleCell}>
                      <span
                        className={`${styles.roleBadge} ${roleBadgeClass(
                          user.role
                        )}`}
                      >
                        {user.role === "owner" ? (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                              <g
                                id="SVGRepo_tracerCarrier"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              ></g>
                              <g id="SVGRepo_iconCarrier">
                                <g
                                  id="🔍-Product-Icons"
                                  stroke="none"
                                  strokeWidth="1"
                                  fill="none"
                                  fillRule="evenodd"
                                >
                                  <g
                                    id="ic_fluent_owner_24_filled"
                                    fill="currentColor"
                                    fillRule="nonzero"
                                  >
                                    <path
                                      d="M17.0016031,15.2440856 L17.0009052,21.2451182 C17.0009052,21.8527788 16.3161092,22.2081862 15.8192057,21.8584172 L12.0007623,19.1706254 L8.18435794,21.8583162 C7.68747081,22.2082475 7.00251516,21.8528589 7.00251516,21.2451182 L7.00069412,15.2459273 C8.37018531,16.3435035 10.1084262,17 12,17 C13.8926316,17 15.6317588,16.3427691 17.0016031,15.2440856 Z M12,2 C15.8659932,2 19,5.13400675 19,9 C19,12.8659932 15.8659932,16 12,16 C8.13400675,16 5,12.8659932 5,9 C5,5.13400675 8.13400675,2 12,2 Z"
                                      id="🎨-Color"
                                    ></path>
                                  </g>
                                </g>
                              </g>
                            </svg>
                            {t("userInfoPage.activityStatus.roles.owner")}
                          </>
                        ) : user.role === "admin" ? (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M12 14v8H4a8 8 0 0 1 8-8zm0-1c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm9 4h1v5h-8v-5h1v-1a3 3 0 0 1 6 0v1zm-2 0v-1a1 1 0 0 0-2 0v1h2z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.activityStatus.roles.admin")}
                          </>
                        ) : (
                          <>
                            <svg
                              width="16"
                              height="16"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M2 22a8 8 0 1 1 16 0H2zm8-9c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm7.363 2.233A7.505 7.505 0 0 1 22.983 22H20c0-2.61-1-4.986-2.637-6.767zm-2.023-2.276A7.98 7.98 0 0 0 18 7a7.964 7.964 0 0 0-1.015-3.903A5 5 0 0 1 21 8a4.999 4.999 0 0 1-5.66 4.957z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.activityStatus.roles.user")}
                          </>
                        )}
                      </span>
                    </td>
                    <td className={styles.statusCell}>
                      <span
                        className={`${styles.statusText} ${
                          user.isActive
                            ? styles.statusTextActive
                            : styles.statusTextInactive
                        }`}
                      >
                        {user.isActive
                          ? t("userInfoPage.activityStatus.status.active")
                          : t("userInfoPage.activityStatus.status.inactive")}
                      </span>
                    </td>
                    <td className={styles.lastLoginCell}>
                      {user.lastLogin
                        ? new Date(user.lastLogin).toLocaleDateString()
                        : t("userInfoPage.activityStatus.never")}
                    </td>
                    <td className={styles.joinedCell}>
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className={styles.mobileCardLayout}>
            <div className={styles.usersGrid}>
              {allUsers.map((user) => (
                <div key={user._id} className={styles.userCard}>
                  <div className={styles.userHeader}>
                    <div className={styles.userInfo}>
                      <div className={styles.userAvatar}>
                        {user.profileImage ? (
                          <img
                            src={user.profileImage}
                            alt={user.name || "User"}
                            className={styles.profileImage}
                            onError={(e) => {
                              e.currentTarget.onerror = null;
                              e.currentTarget.src = DefaultUserImage;
                            }}
                          />
                        ) : (
                          (user.name || "U").charAt(0).toUpperCase()
                        )}
                      </div>
                      <div className={styles.userDetails}>
                        <div className={styles.userName}>
                          {user.name || "No Name"}
                        </div>
                        <div className={styles.userEmail}>{user.email}</div>
                      </div>
                    </div>
                    <span
                      className={`${styles.statusBadge} ${
                        user.isActive
                          ? styles.statusBadgeActive
                          : styles.statusBadgeInactive
                      }`}
                    >
                      {user.isActive
                        ? t("userInfoPage.activityStatus.status.active")
                        : t("userInfoPage.activityStatus.status.inactive")}
                    </span>
                  </div>

                  <div className={styles.userMeta}>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t("userInfoPage.activityStatus.mobileLabels.role")}:
                      </span>
                      <span
                        className={`${styles.roleBadge} ${roleBadgeClass(
                          user.role
                        )}`}
                      >
                        {user.role === "owner" ? (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              version="1.1"
                              xmlns="http://www.w3.org/2000/svg"
                              xmlnsXlink="http://www.w3.org/1999/xlink"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
                              <g
                                id="SVGRepo_tracerCarrier"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              ></g>
                              <g id="SVGRepo_iconCarrier">
                                <g
                                  id="🔍-Product-Icons"
                                  stroke="none"
                                  strokeWidth="1"
                                  fill="none"
                                  fillRule="evenodd"
                                >
                                  <g
                                    id="ic_fluent_owner_24_filled"
                                    fill="currentColor"
                                    fillRule="nonzero"
                                  >
                                    <path
                                      d="M17.0016031,15.2440856 L17.0009052,21.2451182 C17.0009052,21.8527788 16.3161092,22.2081862 15.8192057,21.8584172 L12.0007623,19.1706254 L8.18435794,21.8583162 C7.68747081,22.2082475 7.00251516,21.8528589 7.00251516,21.2451182 L7.00069412,15.2459273 C8.37018531,16.3435035 10.1084262,17 12,17 C13.8926316,17 15.6317588,16.3427691 17.0016031,15.2440856 Z M12,2 C15.8659932,2 19,5.13400675 19,9 C19,12.8659932 15.8659932,16 12,16 C8.13400675,16 5,12.8659932 5,9 C5,5.13400675 8.13400675,2 12,2 Z"
                                      id="🎨-Color"
                                    ></path>
                                  </g>
                                </g>
                              </g>
                            </svg>
                            {t("userInfoPage.activityStatus.roles.owner")}
                          </>
                        ) : user.role === "admin" ? (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M12 14v8H4a8 8 0 0 1 8-8zm0-1c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm9 4h1v5h-8v-5h1v-1a3 3 0 0 1 6 0v1zm-2 0v-1a1 1 0 0 0-2 0v1h2z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.activityStatus.roles.admin")}
                          </>
                        ) : (
                          <>
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="currentColor"
                              className={styles.roleIcon}
                            >
                              <g>
                                <path fill="none" d="M0 0h24v24H0z"></path>
                                <path d="M2 22a8 8 0 1 1 16 0H2zm8-9c-3.315 0-6-2.685-6-6s2.685-6 6-6 6 2.685 6 6-2.685 6-6 6zm7.363 2.233A7.505 7.505 0 0 1 22.983 22H20c0-2.61-1-4.986-2.637-6.767zm-2.023-2.276A7.98 7.98 0 0 0 18 7a7.964 7.964 0 0 0-1.015-3.903A5 5 0 0 1 21 8a4.999 4.999 0 0 1-5.66 4.957z"></path>
                              </g>
                            </svg>
                            {t("userInfoPage.activityStatus.roles.user")}
                          </>
                        )}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t(
                          "userInfoPage.activityStatus.mobileLabels.lastLogin"
                        )}
                        :
                      </span>
                      <span className={styles.metaValue}>
                        {user.lastLogin
                          ? new Date(user.lastLogin).toLocaleDateString()
                          : t("userInfoPage.activityStatus.never")}
                      </span>
                    </div>
                    <div className={styles.metaItem}>
                      <span className={styles.metaLabel}>
                        {t("userInfoPage.activityStatus.mobileLabels.joined")}:
                      </span>
                      <span className={styles.metaValue}>
                        {user.createdAt
                          ? new Date(user.createdAt).toLocaleDateString()
                          : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {!loadingUsers &&
        !usersError &&
        invitations &&
        invitations.length > 0 && (
          <div className={styles.tableContainer}>
            <h3 className={styles.subsectionTitle}>
              {t("userInfoPage.activityStatus.pendingInvitations")}
            </h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>
                    {t(
                      "userInfoPage.activityStatus.tableHeadersInvitations.email"
                    )}
                  </th>
                  <th>
                    {t(
                      "userInfoPage.activityStatus.tableHeadersInvitations.account"
                    )}
                  </th>
                  <th>
                    {t(
                      "userInfoPage.activityStatus.tableHeadersInvitations.invited"
                    )}
                  </th>
                  <th>
                    {t(
                      "userInfoPage.activityStatus.tableHeadersInvitations.expires"
                    )}
                  </th>
                  <th>
                    {t(
                      "userInfoPage.activityStatus.tableHeadersInvitations.actions"
                    )}
                  </th>
                </tr>
              </thead>
              <tbody>
                {invitations
                  .filter((invitation) => invitation.status === "pending")
                  .map((invitation) => (
                    <tr key={invitation._id}>
                      <td className={styles.emailCell}>{invitation.email}</td>
                      <td className={styles.statusCell}>
                        <span
                          className={`${styles.statusBadge} ${styles.statusBadgePending}`}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                          >
                            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z" />
                          </svg>
                          {t("userInfoPage.activityStatus.status.pending")}
                        </span>
                      </td>
                      <td className={styles.joinedCell}>
                        {new Date(invitation.createdAt).toLocaleDateString()}
                      </td>
                      <td className={styles.joinedCell}>
                        {new Date(invitation.expiresAt).toLocaleDateString()}
                      </td>
                      <td className={styles.actionsCell}>
                        <div className={styles.actionButtons}>
                          <button
                            type="button"
                            className={styles.cancelInvitationButton}
                            onClick={() =>
                              handleCancelInvitation(
                                invitation._id,
                                invitation.email
                              )
                            }
                          >
                            {t("cancel")}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}

      {!loadingUsers &&
        !usersError &&
        (!allUsers || allUsers.length === 0) &&
        (!invitations || invitations.length === 0) && (
          <div className={styles.noUsers}>
            <p>{t("userInfoPage.activityStatus.noUsers")}</p>
            <button
              type="button"
              className={styles.noUsersButton}
              onClick={() => {
                hasFetchedRef.current = false;
                fetchUsers();
              }}
            >
              {t("userInfoPage.activityStatus.refreshUsers")}
            </button>
          </div>
        )}
    </div>
  );
}
